import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Anchor } from './Anchor'
import { Marker, MarkerContent, MarkerIcon } from './marker'
import { Spinner } from './spinner'

const buttonVariants = cva(
  "border-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-brand-600 border-2 border-primary hover:border-brand-600',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 border-2 border-destructive hover:border-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 dark:border-destructive/60',
        outline:
          'border-2 bg-surface hover:bg-brand/15 dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        outlineThin:
          'border-1 bg-surface hover:bg-brand/15 dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        ghost: 'hover:bg-brand/15 hover:text-accent-foreground dark:hover:bg-accent/50 border-none',
        link: 'text-ink-600 border-none hover:text-ink',
        iconOnly: 'bg-transparent hover:bg-transparent border-none',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-4 text-sm has-[>svg]:px-3',
        lg: 'h-[50px] px-6 text-md has-[>svg]:px-5',
        xs: 'h-6 gap-1 px-2 has-[>svg]:px-1.5 text-xs',
        xlg: 'h-15 px-10 has-[>svg]:px-8 text-md',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    compoundVariants: [
      // Extra-small outline buttons read as too heavy at 2px — thin the border to match the smaller scale.
      { variant: 'outline', size: 'xs', className: 'border' },
      { variant: 'outlineThin', size: 'xs', className: 'border' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  href,
  target,
  loading,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    href?: string
    target?: string
    /** When truthy, renders a spinner + this text (shimmering) instead of children and disables the button. */
    loading?: React.ReactNode
  }) {
  const Comp = asChild ? Slot : 'button'
  const isLoading = Boolean(loading)

  const content = isLoading ? (
    <Marker role="status">
      <MarkerIcon>
        {/* Decorative: the visible text + role="status" already convey loading,
            so keep the spinner out of the button's accessible name. */}
        <Spinner aria-hidden />
      </MarkerIcon>
      <MarkerContent className="thinking-shimmer thinking-shimmer-brand">{loading}</MarkerContent>
    </Marker>
  ) : (
    children
  )

  if (href) {
    // Forward the caller's remaining props (aria-label, title, download, …), not
    // just onClick: dropping them left icon-only links with no accessible name.
    // Spread first so href/target/rel/className below can't be overridden.
    const anchorProps = props as React.ComponentProps<'a'>
    const opensNewTab = href.startsWith('http') || target === '_blank'
    return (
      <Anchor
        {...anchorProps}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        href={href}
        className={cn(buttonVariants({ variant, size, className }))}
        {...(opensNewTab ? { target: '_blank' as const, rel: 'noopener noreferrer' } : { target })}
      >
        {content}
      </Anchor>
    )
  }

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={isLoading || undefined}
      className={cn(
        buttonVariants({ variant, size }),
        // Disabled buttons keep their variant color at 50% opacity (base style).
        // While loading, dim only slightly instead — the button stays disabled.
        isLoading && 'disabled:opacity-90',
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {content}
    </Comp>
  )
}

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>

export { Button, buttonVariants }
